/**
 * The change-request panel.
 *
 * Browsing, not acting: the engine splits the read (`ListChangeRequests`) from
 * the mutation (`ChangeRequestAction`), and only the read is on the web
 * allowlist. A leaked browser token must not be able to merge anything, so this
 * panel links out to the provider rather than offering a merge button.
 *
 * It is a right-hand pane on a wide screen and a sheet on a narrow one, matching
 * T3's "Open a surface" panel.
 */

import { useEffect, useState } from "react";
import { useAppState, useStore } from "./store-context";
import { IconBranch, IconChevronRight } from "./icons";
import type { ChangeRequestSummary } from "../app/store";

export function ChangeRequestPanel({ onClose }: { onClose: () => void }) {
  const state = useAppState();
  const store = useStore();
  const list = state.changeRequests;
  const [stateFilter, setStateFilter] = useState<"open" | "closed" | "all">("open");

  useEffect(() => {
    void store.loadChangeRequests(stateFilter);
  }, [store, stateFilter, state.openChatId]);

  return (
    <aside className="flex min-h-0 w-full shrink-0 flex-col border-l border-border bg-background lg:w-[22rem]">
      <header className="flex h-[var(--workspace-topbar-height)] shrink-0 items-center gap-2 border-b border-border px-3">
        <h2 className="min-w-0 flex-1 truncate text-sm font-medium">
          {list !== undefined && list.repository.length > 0 ? list.repository : "Change requests"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent"
        >
          ×
        </button>
      </header>

      <div className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-1.5">
        {(["open", "closed", "all"] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setStateFilter(option)}
            className={`rounded-full px-2.5 py-1 text-2xs capitalize ${
              option === stateFilter
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-accent"
            }`}
          >
            {option}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {list === undefined ? (
          <p className="px-2 py-6 text-center text-xs text-muted-foreground">
            This conversation has no checkout to read change requests from.
          </p>
        ) : list.items.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-muted-foreground">
            No {stateFilter === "all" ? "" : `${stateFilter} `}change requests.
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {list.items.map((item) => (
              <ChangeRequestRow key={`${item.provider}:${item.number}`} item={item} />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

/** One row. Opens the provider's page in a new tab — the app does not host a
 * diff view, and pretending to is how a browse surface becomes a worse copy of
 * the provider's own UI. */
function ChangeRequestRow({ item }: { item: ChangeRequestSummary }) {
  const tone =
    item.state === "merged"
      ? "text-success-foreground"
      : item.state === "closed"
        ? "text-error-foreground"
        : "text-info";
  return (
    <li>
      <a
        href={item.url}
        target="_blank"
        rel="noreferrer noopener"
        className="flex flex-col gap-1 rounded-lg px-2 py-1.5 hover:bg-accent"
      >
        <span className="flex min-w-0 items-start gap-1.5">
          <span className={`shrink-0 font-mono text-xs ${tone}`}>#{item.number}</span>
          <span className="min-w-0 flex-1 text-sm text-foreground">{item.title}</span>
          <IconChevronRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        </span>
        <span className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <IconBranch className="size-3" />
          <span className="truncate">
            {item.headRef} → {item.baseRef}
          </span>
          {item.isDraft ? (
            <span className="shrink-0 rounded bg-muted px-1">draft</span>
          ) : null}
          {item.state === "merged" ? <span className="shrink-0">merged</span> : null}
        </span>
      </a>
    </li>
  );
}

/** A compact count for the chat header, so the panel is discoverable without
 * being open. */
export function ChangeRequestButton({ onOpen }: { onOpen: () => void }) {
  const state = useAppState();
  const count = state.changeRequests?.items.length ?? 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center gap-1 rounded-full border border-border px-2 py-1 text-2xs text-muted-foreground hover:bg-accent"
    >
      <IconBranch className="size-3" />
      {count > 0 ? count : "PRs"}
    </button>
  );
}
